import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Divider,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createBin, updateBin } from "../../services/WarehouseSettings";
import { IBinRequestDTO } from "../../interfaces/WarehouseSettings";
import { formFieldStyles, labelStyles, dialogTitleStyles, dialogDescriptionStyles } from "../../styles/formStyles";
import BarcodeDisplay from "../common/BarcodeDisplay";
import { useAuth } from "../../auth/AuthProvider";

interface AddEditBinProps {
  open: boolean;
  onClose: () => void;
  editData: any;
  buildingId: string;
  floorId: string;
  roomId: string;
  rackId: string;
  prefilledRow?: number;
  prefilledCol?: number;
  onSuccess: (message: string) => void;
}

const AddEditBin: React.FC<AddEditBinProps> = ({
  open,
  onClose,
  editData,
  buildingId,
  floorId,
  roomId,
  rackId,
  prefilledRow,
  prefilledCol,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    row: "",
    coloumn: "",
    lengthMtr: "",
    widthMtr: "",
    heightMtr: "",
    barcode: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const WAREHOUSE_ID = loggedInUser?.workspaceId;
  const COMPANY_ID = loggedInUser?.companyId;

  useEffect(() => {
    if (editData && open) {
      const newFormData = {
        row: editData.row || editData.rows || "",
        coloumn: editData.coloumn || editData.cols || "",
        lengthMtr: editData.lengthMtr || editData.length || "",
        widthMtr: editData.widthMtr || editData.width || "",
        heightMtr: editData.heightMtr || editData.height || "",
        barcode: editData.barcode || "",
      };
      setFormData(newFormData);
    } else if (!editData) {
      setFormData({
        row: prefilledRow ? String(prefilledRow) : "",
        coloumn: prefilledCol ? String(prefilledCol) : "",
        lengthMtr: "",
        widthMtr: "",
        heightMtr: "",
        barcode: "",
      });
    }
    setErrors({});
  }, [editData, open, prefilledRow, prefilledCol]);

  const validate = () => {
    const newErrors: any = {};
    if (!formData.row) newErrors.row = "Row is required";
    else if (Number(formData.row) <= 0) newErrors.row = "Row must be greater than 0";
    if (!formData.coloumn) newErrors.coloumn = "Column is required";
    else if (Number(formData.coloumn) <= 0) newErrors.coloumn = "Column must be greater than 0";
    if (!formData.lengthMtr) newErrors.lengthMtr = "Length is required";
    else if (Number(formData.lengthMtr) <= 0) newErrors.lengthMtr = "Length must be greater than 0";
    if (!formData.widthMtr) newErrors.widthMtr = "Width is required";
    else if (Number(formData.widthMtr) <= 0) newErrors.widthMtr = "Width must be greater than 0";
    if (!formData.heightMtr) newErrors.heightMtr = "Height is required";
    else if (Number(formData.heightMtr) <= 0) newErrors.heightMtr = "Height must be greater than 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!COMPANY_ID || !WAREHOUSE_ID) {
      setSnackbarMessage("User information not available");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    setLoading(true);
    try {
      const isEdit = Boolean(editData?.id);
      
      const dto: IBinRequestDTO = {
        companyId: COMPANY_ID,
        warehouseId: WAREHOUSE_ID,
        buildingId: Number(buildingId),
        floorId: Number(floorId),
        roomId: Number(roomId),
        rackId: Number(rackId),
        row: Number(formData.row),
        coloumn: Number(formData.coloumn),
        lengthMtr: Number(formData.lengthMtr),
        widthMtr: Number(formData.widthMtr),
        heightMtr: Number(formData.heightMtr),
      };

      const response = isEdit 
        ? await updateBin(editData.id, dto)
        : await createBin(dto);

      if (response && response.status === "success") {
        const message = isEdit ? "Bin updated successfully" : "Bin added successfully";
        onSuccess(message);
        onClose();
      } else {
        const errorMsg = response?.errorResponse?.response?.data?.message || 
                         response?.errorResponse?.message || 
                         `Error ${isEdit ? 'updating' : 'creating'} bin`;
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setSnackbarMessage(err.message || err);
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography sx={dialogTitleStyles}>
            {editData ? "Edit Bin" : "Add Bin"}
          </Typography>
          <Typography sx={dialogDescriptionStyles}>
            {editData ? "Edit Bin" : "Add Bin"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>
              Row <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="1"
              value={formData.row}
              onChange={(e) => {
                setFormData({ ...formData, row: e.target.value });
                setErrors({ ...errors, row: "" });
              }}
              error={!!errors.row}
              helperText={errors.row}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography component="label" sx={labelStyles}>
              Column <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="1"
              value={formData.coloumn}
              onChange={(e) => {
                setFormData({ ...formData, coloumn: e.target.value });
                setErrors({ ...errors, coloumn: "" });
              }}
              error={!!errors.coloumn}
              helperText={errors.coloumn}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography component="label" sx={labelStyles}>
              Dimension (L x W x H m) <span style={{ color: "red" }}>*</span>
            </Typography>
          </Grid>
          
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="1"
              value={formData.lengthMtr}
              onChange={(e) => {
                setFormData({ ...formData, lengthMtr: e.target.value });
                setErrors({ ...errors, lengthMtr: "" });
              }}
              error={!!errors.lengthMtr}
              helperText={errors.lengthMtr}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="1"
              value={formData.widthMtr}
              onChange={(e) => {
                setFormData({ ...formData, widthMtr: e.target.value });
                setErrors({ ...errors, widthMtr: "" });
              }}
              error={!!errors.widthMtr}
              helperText={errors.widthMtr}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="1"
              value={formData.heightMtr}
              onChange={(e) => {
                setFormData({ ...formData, heightMtr: e.target.value });
                setErrors({ ...errors, heightMtr: "" });
              }}
              error={!!errors.heightMtr}
              helperText={errors.heightMtr}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>

          {editData && formData.barcode && (
            <Grid item xs={12}>
              <BarcodeDisplay value={formData.barcode} />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <Divider sx={{ml:2, mr:2}}/>
      <DialogActions sx={{ ml:1, mr:1, justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
            padding: "8px 16px",
            textTransform: "none",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          sx={{
            borderRadius: "6px",
            border: "1px solid",
            borderColor: "primary.main",
            boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            padding: "8px 40px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "primary.main",
              boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset",
            },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : (editData ? "Save" : "Add")}
        </Button>
      </DialogActions>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddEditBin;